<template>
  <div class="activity-duration">
    <div class="activity-duration__title">
      <p>{{ $t('routeOptimization.fields.activityDuration') }}</p>
      <i
        id="activity-duration-tooltip"
        class="float-end input-tooltip-btn"
        :class="{
          'icon-outline_info': !isHover,
          'icon-fill_info': isHover,
        }"
        @mouseenter="isHover = true"
        @mouseleave="isHover = false"
      ></i>

      <b-tooltip target="activity-duration-tooltip" triggers="hover" placement="right">
        {{ $t('routeOptimization.tooltip.averagePickupDeliveryDuration') }}
      </b-tooltip>
    </div>

    <div v-for="field in fields" :key="field.key" class="row">
      <div class="activity-duration__section">
        <b-col md="2" class="activity-duration__section-radio">
          <McRadioButton
            :id="field.radioBtn.id"
            v-model="isSelectMerge"
            class="__radio-btn"
            name="activity_duration"
            :label="field.radioBtn.label"
            :initialValue="field.radioBtn.initialValue"
            :fullWidth="false"
            :disabled="field.key === 'eachAddress' ? isDisabledDuration : false"
            @input="() => handleInput({ key: field.key })"
          />
        </b-col>
        <b-col md="6">
          <FormulateInput
            :key="`duration-${field.key}`"
            :ref="`duration-${field.key}-ref`"
            v-model="formValues[field.key]"
            class="__duration-input"
            label=""
            ignored
            type="time-duration"
            placeholder="00 Jam 00 Menit"
            :initialValues="initDurationValues[field.key]"
            :disabled="
              field.key === 'eachAddress' ? isSelectMergeBtnDisabled : !isSelectMergeBtnDisabled
            "
            :optionItems="optionItems"
            validation="isoTimeGteValue:PT3M"
            validation-behavior="live"
            error-behavior="live"
            @input="() => handleDurationInput({ key: field.key })"
          />
        </b-col>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import McRadioButton from '@/components/McRadioButton'
import { Helper } from '@/mixin/helper'

export default {
  components: {
    McRadioButton,
  },
  props: {
    activityDuration: {
      type: Boolean,
      default: false,
    },
    serviceTime: {
      type: String,
      default: 'PT15M',
    },
  },
  emits: ['update:activityDuration', 'update:serviceTime'],
  mixins: [Helper],
  data() {
    return {
      isHover: false,
      isSelectMerge: true,

      formValues: {
        eachAddress: 'PT15M',
        eachDO: 'PT15M',
      },
      initDurationValues: {
        eachAddress: {
          hours: 0,
          minutes: 15,
        },
        eachDO: {
          hours: 0,
          minutes: 15,
        },
      },

      optionItems: [
        {
          key: 'hours',
          items: Array.from({ length: 24 }, (_, i) => i),
        },
        {
          key: 'minutes',
          items: Array.from({ length: 60 }, (_, i) => i),
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
      getFeatureStrategies: 'identity/getFeatureStrategies',
    }),
    isSelectMergeBtnDisabled() {
      if (this.isRoSwatActive) {
        return true
      }
      return this.isSelectMerge ? false : true
    },
    isDisabledDuration() {
      return this.isRoSwatActive
    },
    fields() {
      return [
        {
          key: 'eachAddress',
          radioBtn: {
            id: 'eachAddress',
            label: this.$t('routeOptimization.fields.activityDurationPerAddress'),
            initialValue: true,
          },
          durationInput: {
            key: 'eachAddress',
          },
        },
        {
          key: 'eachDO',
          radioBtn: {
            id: 'eachDO',
            label: this.$t('routeOptimization.fields.activityDurationPerDO'),
            initialValue: false,
          },
          durationInput: {
            key: 'eachDO',
          },
        },
      ]
    },
    isRoSwatActive() {
      const isSamator = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.is_samator || false
      const isActive = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.active || false
      return isSamator && isActive
    },
  },
  watch: {
    activityDuration: {
      handler(newValue) {
        if (newValue) {
          this.isSelectMerge = false
          return
        }
        this.isSelectMerge = true
      },
      immediate: true,
    },
    serviceTime: {
      handler(newValue, oldValue) {
        if (newValue) {
          this.formValues.eachAddress = newValue
          this.formValues.eachDO = newValue
          this.updateInitialDurationValues(newValue)
        }
      },
      immediate: true,
    },
  },
  methods: {
    ResetFormValues() {
      this.formValues = {
        eachAddress: this.serviceTime || 'PT15M',
        eachDO: this.serviceTime || 'PT15M',
      }
    },

    updateInitialDurationValues(isoTime) {
      const match = isoTime.match(/PT(?:(\d+)H)?(?:(\d+)M)?/)
      if (match) {
        const hours = parseInt(match[1]) || 0
        const minutes = parseInt(match[2]) || 0

        this.initDurationValues = {
          eachAddress: {
            hours,
            minutes,
          },
          eachDO: {
            hours,
            minutes,
          },
        }
      }
    },

    handleInput({ key }) {
      const refs = ['eachAddress', 'eachDO']
      refs.forEach((ref) => {
        this.$refs[`duration-${ref}-ref`][0].validationErrors = []
      })

      this.handleDurationInput({ key })

      const isAddressKey = key === 'eachAddress'
      this.$refs[`duration-${key}-ref`][0].performValidation()
      this.isSelectMerge = isAddressKey

      this.$emit('update:activityDuration', isAddressKey ? false : true)
    },

    handleDurationInput({ key }) {
      this.$emit('update:serviceTime', this.formValues[key])
    },
  },
}
</script>

<style lang="scss" scoped>
.activity-duration {
  &__title {
    font-size: 13px;
    display: flex;
    gap: 4px;
    align-items: center;

    p {
      margin: 0;
    }
  }

  &__section {
    display: flex;
    gap: 12px;
    align-items: center;

    .__duration-input {
      width: 300px;
    }
  }

  &__section-radio {
    width: 13.666667%;
  }
}
</style>
